import { z } from 'zod';
/**
 * Stills (Dax, 2026-10-05). A still is an asset of media kind `image`: one JPEG we keep, never the original.
 * The browser converts every photo it can decode (JPEG, PNG and WebP everywhere; HEIC in Safari only), upright
 * and scaled to STILL_LONG_EDGE; a HEIC another browser cannot decode is uploaded as it is and converted by
 * the worker to the same JPEG before anything else reads it. Stills feed Veo or Omni, where the extra quality
 * of an original is lost anyway.
 */
export const MediaKind = z.enum(['video', 'image']);
/** The long edge of the JPEG a still is kept as, in pixels. */
export const STILL_LONG_EDGE = 2048;
/** The browser's JPEG quality for a converted still (canvas `toBlob`, 0 to 1). */
export const STILL_JPEG_QUALITY = 0.85;
/** The worker's JPEG quality for a still it converts (FFmpeg `-q:v`, 2 best to 31 worst); about the browser's 0.85. */
export const STILL_FFMPEG_QSCALE = 3;
/** The public copy Browse, Library and Tagging show, in pixels on its long edge; the kept JPEG stays private. */
export const STILL_WEB_LONG_EDGE = 1280;
/** Photos in one drop: a shoot is dozens, a camera roll hundreds, and each is a small file. */
export const UPLOAD_MAX_IMAGE_FILES = 500;
/**
 * Animate a still (Dax, 2026-10-05): Vertex Veo image-to-video, by hand, metered. The cheapest model that
 * holds a face (Lite; Fast looked no better on a real tasting frame), the shortest length Veo allows, native
 * 720p and no audio, which roughly halves the price. Veo 3 always enhances the prompt; it cannot be turned off.
 */
export const ANIMATE_MODEL = 'veo-3.1-lite-generate-001';
/** Veo is served in us-central1 only. */
export const ANIMATE_LOCATION = 'us-central1';
/** Veo 3.1 accepts 4, 6 or 8 seconds. */
export const ANIMATE_SECONDS = 4;
export const ANIMATE_RESOLUTION = '720p';
/** The frame Veo is given: the still centre-cropped to 9:16 or 16:9, this long, at its native 720p. */
export const ANIMATE_INPUT_LONG_EDGE = 1280;
export const ANIMATE_INPUT_SHORT_EDGE = 720;
/** Vertex list price per second of video-only output at 720p, recorded on each ledger row (2026-10-05). */
export const ANIMATE_PRICE_PER_SECOND_USD = {
    'veo-3.1-lite-generate-001': 0.03,
    'veo-3.1-fast-generate-001': 0.08,
    'veo-3.1-generate-001': 0.2,
};
/** Stills an organisation may animate per calendar month when the operator has set nothing else. */
export const ANIMATE_DEFAULT_ALLOWANCE = 20;
/** How often the worker asks Vertex whether a generation has finished, and when it gives up. */
export const ANIMATE_POLL_MS = 10_000;
export const ANIMATE_DEADLINE_MS = 10 * 60_000;
/**
 * The one standard instruction (Dax, 2026-10-05): very gentle, natural motion, the camera still, nothing cut.
 * Per Google's image-to-video guidance it describes motion only, never what is already in the frame.
 */
export const ANIMATE_PROMPT = 'Static camera. The subject moves very slightly and naturally, as in a real, unposed moment. One continuous shot.';
export const ANIMATE_NEGATIVE_PROMPT = 'camera movement, zoom, pan, fast motion, cut, scene change, transition, morphing, distortion, text, extra people';
/** The Veo shape for a still: upright photos become 9:16, everything else 16:9 (Veo offers no other). */
export function animateShape(width, height) {
    return height > width
        ? { aspectRatio: '9:16', width: ANIMATE_INPUT_SHORT_EDGE, height: ANIMATE_INPUT_LONG_EDGE }
        : { aspectRatio: '16:9', width: ANIMATE_INPUT_LONG_EDGE, height: ANIMATE_INPUT_SHORT_EDGE };
}
/** An organisation's allowance this month: what the operator set, else the default. */
export function animateAllowance(set) {
    return set ?? ANIMATE_DEFAULT_ALLOWANCE;
}
/** Generation states that count against the allowance: refused and failed calls are not charged. */
export const ANIMATE_COUNTED_STATUSES = ['queued', 'running', 'done'];
//# sourceMappingURL=stills.js.map